import {getStream} from '@api/post';
import MoreButton from '@components/MoreButton';
import StreamItem from '@components/StreamItem';
import type GetStreamPostsResponse from '@interfaces/GetStreamPostsResponse';
import type {StreamPost} from '@interfaces/Post';
import Analytics from '@services/analytics/Analytics';
import {unixTimestampToMilli} from '@utils/dateUtils';
import {format} from 'date-fns';
import {ru, enUS} from 'date-fns/locale';
import type {ReactElement} from 'react';
import {useState} from 'react';

interface Props {
    term: string;
    taxonomy: string;
    withTextMarker: boolean;
    posts: StreamPost[];
    hasMorePosts: boolean;
}

export default function Stream(props: Props): ReactElement<Props> {
    const [posts, setPosts] = useState<StreamPost[]>(props.posts);
    const [loading, setLoading] = useState(false);
    const [hasMorePosts, setHasMorePosts] = useState(props.hasMorePosts);

    function handleMoreClick() {
        Analytics.triggerEvent('Клик на «Читать еще истории»', 'click');
        setLoading(true);
        getStream(props.taxonomy, props.term, getLatestTime())
            .then((response: GetStreamPostsResponse) => {
                setPosts([...posts, ...response.data.posts]);
                setHasMorePosts(response.data.hasMorePosts);
                setLoading(false);
            })
            .catch((error: unknown) => {
                setLoading(false);
                console.error(error);
            });
    }

    function getLatestTime(): number {
        if (posts.length > 0) {
            return posts.map(el => el.time).sort((a, b) => a - b)[0];
        }
        return 0;
    }

    const currentDate = new Date();
    const renderedPosts = posts.map((element: StreamPost) => (
        <StreamItem
            key={element.title}
            time={element.time}
            title={element.title}
            link={element.link}
            newsMarker={element.marker}
            textMainMarker={element.tag}
            textSubMarker={element.subtag}
            withTextMarker={props.withTextMarker}
        />
    ));
    let lastDate = new Date(unixTimestampToMilli(getLatestTime()));
    const results = [];

    for (let i = 0; i < renderedPosts.length; i++) {
        const date = new Date(unixTimestampToMilli(posts[i].time));

        if (
            lastDate.getDate() !== date.getDate() ||
            lastDate.getMonth() !== date.getMonth() ||
            lastDate.getFullYear() !== date.getFullYear()
        ) {
            const dateFormat =
                date.getFullYear() === currentDate.getFullYear()
                    ? 'd MMMM, iiii'
                    : 'd MMMM y, iiii';
            results.push(
                <div className="stream__headline" key={-i}>
                    {format(date, dateFormat, {
                        locale: window.bootstrapParams.language === 'en' ? enUS : ru,
                    })}
                </div>,
            );
            lastDate = date;
        }
        results.push(renderedPosts[i]);
    }

    return (
        <div>
            {results}
            {hasMorePosts ? (
                <div className="stream__button">
                    <MoreButton onClick={handleMoreClick} loading={loading} />
                </div>
            ) : null}
        </div>
    );
}
